import { CurrencyPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom, Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { TransactionsAcServiceService } from 'src/app/retail/services/transactions-ac-service.service';
import { EstadosTransferenciaManual } from 'src/app/shared/enums/estados-transferencia-manual.enum';
import { UserRoles } from 'src/app/shared/enums/roles-enum';
import { UtcToLocalTimeFormat } from 'src/app/shared/pipes/utc-converter.service';
import { UtcToLocalTimePipe } from 'src/app/shared/pipes/utc-to-local-time.pipe';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { ManualTransaction } from '../../interfaces/manual-transaction';
import { TransactionUserDTO } from '../../interfaces/transaction-user.dto';
import {
  BinanceWithdrawalService,
  WithdrawalHistoryItem,
} from '../../services/binance-withdrawal.service';
import { ManualTransactionService } from '../../services/manual-transaction.service';
import { TransaccionService } from '../../services/transaccion.service';
import { WalletVesClientService, VesMovementItem, UsdtCardexItem, UsdtWithdrawalItem, UsdtPendingSwapItem } from '../../services/wallet-ves-client.service';
import { tieneSaldoVes } from 'src/app/shared/utils/saldo-ves';

export interface UnifiedUsdtMovement {
  source: 'cardex' | 'withdrawal';
  date: string;
  label: string;
  amount: number;
  isCredit: boolean;
  status: string;
  haber?: number;
  debe?: number;
  saldo?: number;
  description?: string | null;
  network?: string;
  walletAddress?: string;
  fee?: number;
  netAmount?: number;
  binanceTxId?: string | null;
  rejectionReason?: string | null;
}

@Component({
  selector: 'app-transactions',
  templateUrl: './transactions.component.html',
  styleUrls: ['./transactions.component.sass'],
})
export class TransactionsComponent implements OnInit, OnDestroy {
  view: 'historial' | 'lista manuales' = 'historial';
  viewInfo: boolean = false;
  manualTransactions: ManualTransaction[] = [];
  subscription: Subscription = new Subscription();
  userRole: UserRoles = UserRoles.aguacate_client;
  user: any;
  cognitoUser: any;
  nombreUsuario: string = '';
  userEnum = UserRoles;
  estadoValidacion: number = -1;
  saldoActual = 0;
  listaTransacciones: TransactionUserDTO[] = [];
  dataSourceHistorial: MatTableDataSource<any> | undefined;
  public currentPage = 1;
  public pageSize = 5;
  public totalItems = 0;
  public totalPages = this.totalItems / this.pageSize;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  expandedManualId: string | null = null;
  expandedCardIndex: number | null = null;

  // VES
  tipoId: string = '';
  /**
   * Historial que ve el chileno con saldo VES: el CLP de siempre o sus
   * movimientos VES. Entra en VES con `?moneda=VES` (tarjeta VES del home).
   */
  monedaHistorial: 'CLP' | 'VES' = 'CLP';

  get esChilenoConVes(): boolean {
    // Perfil CL con saldo VES: familia CL y también CCVE/V o un tipo vacío con
    // cédula V/E (`puedeTenerSaldoVes` de Nest).
    return this.tipoId !== 'VE' && tieneSaldoVes(this.tipoId, this.user?.numeroId);
  }
  movimientosVes: VesMovementItem[] = [];
  currentPageVes: number = 1;
  pageSizeVes: number = 10;
  totalMovimientosVes: number = 0;
  totalPagesVes: number = 0;
  loadingVes: boolean = false;
  expandedVesIndex: number | null = null;

  // USDT (VE users use cardex, others use withdrawals)
  movimientosUsdt: WithdrawalHistoryItem[] = [];
  movimientosUsdtCardex: UsdtCardexItem[] = [];
  usdtWithdrawals: UsdtWithdrawalItem[] = [];
  usdtPendingSwaps: UsdtPendingSwapItem[] = [];
  unifiedUsdtMovements: UnifiedUsdtMovement[] = [];
  currentPageUsdt: number = 1;
  pageSizeUsdt: number = 10;
  totalMovimientosUsdt: number = 0;
  loadingUsdt: boolean = false;
  expandedUsdtIndex: number | null = null;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private route: ActivatedRoute,
    private transaccionService: TransaccionService,
    private manualTransaccionService: ManualTransactionService,
    private datePipe: UtcToLocalTimePipe,
    private currencyPipe: CurrencyPipe,
    private spinner: SpinnerService,
    private auth: CognitoService,
    private transactionsAc: TransactionsAcServiceService,
    private walletVesClientService: WalletVesClientService,
    private binanceWithdrawalService: BinanceWithdrawalService,
  ) {
    let spinnerRef = this.spinner.start();
    this.view = 'historial';
    this.subscription.add(
      this.store.select('auth').subscribe(async ({ user }: any) => {
        this.cognitoUser = await this.auth.getUser();
        this.user = user;
        this.tipoId = user?.tipoId || '';
        if (user && user.usuarioId && this.cognitoUser) {
          if (this.tipoId === 'VE') {
            this.obtenerMovimientosVes();
            this.obtenerMovimientosUsdt();
          } else {
            await this.loadTransactions();
            if (this.esChilenoConVes) this.obtenerMovimientosVes();
          }
          this.manualTransactions =
            await this.manualTransaccionService.getAllTransactionsByUser(
              user.usuarioId,
            );
          this.manualTransactions = this.manualTransactions.filter(
            (transaction) =>
              transaction.estado === EstadosTransferenciaManual.ABIERTA,
          );
          this.dataSourceHistorial = new MatTableDataSource(
            this.manualTransactions,
          );
        }
        this.validateManualTransactions();
      }),
    );
    this.spinner.stop(spinnerRef);
  }

  validateManualTransactions() {
    if (this.manualTransactions.length > 0) {
      this.viewInfo = true;
    }
  }

  async ngOnInit(): Promise<void> {
    // `?moneda=VES|CLP` elige el historial del chileno, también si cambia sin
    // salir de la pantalla. Solo lo usa la vista del chileno (`esChilenoConVes`).
    this.subscription.add(
      this.route.queryParamMap.subscribe((params) => {
        const moneda = String(params.get('moneda') ?? '').toUpperCase();
        if (moneda === 'VES' || moneda === 'CLP') this.monedaHistorial = moneda;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onListaManualesClick() {
    this.view = 'lista manuales';
  }

  onHistorialClick() {
    this.view = 'historial';
  }

  onPageChange(event: any) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex + 1;
    this.loadTransactions();
  }

  async loadTransactions() {
    const ref = this.spinner.start();
    try {
      const data = await this.transaccionService.getTransaccionCliente(
        this.currentPage,
        this.pageSize,
        this.user.usuarioId,
      );
      this.listaTransacciones = this.colapsarDevoluciones(data.query || data);
      this.totalItems = parseInt(data.count) || this.listaTransacciones.length;
    } finally {
      this.spinner.stop(ref);
    }
  }

  /**
   * Un envío CLP→VES rechazado llega como DOS filas y se ve como si la
   * operación se hubiera hecho dos veces. Acá se colapsa a una.
   *
   * Por qué llegan dos: la lista no sale de `transacciones`, sale del libro de
   * saldos. Rechazar deja dos filas con el mismo `order_id` —el cargo
   * (`transaction.service.ts:1454`) y el abono de reversión
   * (`reject-transaction.ts:76`)— y la query las une con la MISMA transacción,
   * así que las dos traen el mismo beneficiario, monto, tasa y estado.
   *
   * Se exigen DOS señales, no una, porque el precio de equivocarse es ocultarle
   * a un cliente una operación real:
   *
   *  1. **El par.** Las dos filas comparten todo lo que viene de la
   *     transacción (`fechaInicio`, beneficiario, montos, tasa). Si el grupo no
   *     tiene exactamente dos filas, no se toca nada.
   *  2. **El comentario.** De las tres columnas propias de la fila de balance
   *     que la query expone, `comentarios` es la única que las distingue: el
   *     cargo trae `Envio VE`, la reversión lo trae vacío. Si no hay
   *     exactamente una candidata, no se toca nada.
   *
   * Sola, ninguna de las dos alcanza: el par no distingue cuál es cuál, y el
   * comentario vacío también lo escribe el cron que ejecuta transferencias que
   * esperaban depósito (`check-pending-transactions.ts:43`). Juntas, para
   * equivocarse harían falta dos envíos distintos creados en el mismo segundo,
   * al mismo beneficiario, por el mismo monto y a la misma tasa — y aun así uno
   * de los dos tendría que tener el comentario vacío.
   *
   * Límite conocido: la lista pagina de 5 en 5 desde el backend, así que un par
   * partido entre dos páginas no se colapsa. Queda una fila en cada página, que
   * es aceptable — no quedan una al lado de la otra y no se oculta nada.
   *
   * Esto se cae solo cuando la query exponga `b2.motivo` como columna propia:
   * ahí la reversión se reconoce por sí misma, sin emparejar ni inferir.
   */
  private colapsarDevoluciones(
    filas: TransactionUserDTO[],
  ): TransactionUserDTO[] {
    if (!Array.isArray(filas)) return filas;

    const esRechazoFiatClp = (t: any) =>
      t?.tipoTransaccion === 'FIAT' &&
      t?.tipoMonedaOrigen === 'CLP' &&
      (t?.estadoActual || '')
        .toUpperCase()
        .replaceAll(' ', '_')
        .includes('RECHAZAD');

    const claveOperacion = (t: any) =>
      [t.fechaInicio, t.beneficiario, t.monedaOrigen, t.monedaDestino, t.tn].join(
        '|',
      );

    const grupos = new Map<string, TransactionUserDTO[]>();
    for (const fila of filas) {
      if (!esRechazoFiatClp(fila)) continue;
      const clave = claveOperacion(fila);
      const grupo = grupos.get(clave) || [];
      grupo.push(fila);
      grupos.set(clave, grupo);
    }

    const ocultar = new Set<TransactionUserDTO>();
    for (const grupo of grupos.values()) {
      if (grupo.length !== 2) continue;
      const devoluciones = grupo.filter(
        (f) => !((f as any).comentarios || '').trim(),
      );
      if (devoluciones.length !== 1) continue;
      ocultar.add(devoluciones[0]);
      // El cargo que queda sabe que su devolución fue en CLP. Un rechazo sin
      // este par (solo el cargo) es de un chileno con devolución en VES.
      for (const f of grupo) if (f !== devoluciones[0]) (f as any).devolucionEnClp = true;
    }

    return ocultar.size ? filas.filter((f) => !ocultar.has(f)) : filas;
  }

  sortDataHistory(sort: Sort) {
    const data = this.manualTransactions.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSourceHistorial = new MatTableDataSource(data);
    }

    this.manualTransactions = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'totalEnvio':
          return this.compare(a.totalEnvio, b.totalEnvio, isAsc);
        case 'totalDest':
          return this.compare(a.totalDest, b.totalDest, isAsc);
        case 'valorPagado':
          return this.compare(a.valorPagado, b.valorPagado, isAsc);
        case 'tasa':
          return this.compare(a.tasa, b.tasa, isAsc);
        case 'fechaCreacion':
          return this.compare(
            a.fechaCreacion.toString(),
            b.fechaCreacion.toString(),
            isAsc,
          );
        case 'fechaFin':
          return this.compare(
            a.fechaFin.toString(),
            b.fechaFin.toString(),
            isAsc,
          );
        case 'fechaActualizacion':
          return this.compare(
            a.fechaActualizacion.toString(),
            b.fechaActualizacion.toString(),
            isAsc,
          );
        case 'estado':
          return this.compare(a.estado, b.estado, isAsc);
        default:
          return 0;
      }
    });
    this.dataSourceHistorial = new MatTableDataSource(this.manualTransactions);
  }

  compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  toggleHistorialCard(index: number): void {
    this.expandedCardIndex = this.expandedCardIndex === index ? null : index;
  }

  toggleManualCard(id: string): void {
    this.expandedManualId = this.expandedManualId === id ? null : id;
  }

  getEstadoManualBg(estado: string): string {
    switch (estado) {
      case 'ABIERTA':
        return '#f59e0b';
      case 'CERRADA':
        return '#16a34a';
      default:
        return '#6b7280';
    }
  }

  displayedColumnsHistorial: string[] = [
    'totalEnvio',
    'monedaLocal',
    'totalDest',
    'monedaDestino',
    'valorPagado',
    'tasa',
    'walletTrx',
    'estado',
    'fechaCreacion',
    'fechaFin',
    'fechaActualizacion',
  ];

  columnsHistorial = [
    {
      columnDef: 'id',
      header: 'Id',
      cell: (element: any) => `${element.id}`,
      headerStyle: {
        'text-align': 'center',
        'font-weight': 'bold',
        'max-width': '220px',
        'min-width': '220px',
      },
      cellStyle: {
        'text-align': 'center',
        'max-width': '220px',
        'min-width': '220px',
      },
    },
    {
      columnDef: 'totalEnvio',
      header: 'Monto Promesa',
      cell: (element: any) =>
        `${this.currencyPipe.transform(element.totalEnvio, 'USD', 'symbol-narrow', '1.2-2', 'en-US')}`,
      headerStyle: {
        'text-align': 'center',
        'font-weight': 'bold',
        'max-width': '220px',
        'min-width': '180px',
      },
      cellStyle: {
        'text-align': 'left',
        'max-width': '220px',
        'min-width': '180px',
      },
    },
    {
      columnDef: 'totalDest',
      header: 'Total Destino',
      cell: (element: any) =>
        `${this.currencyPipe.transform(element.totalDest, 'USD', 'symbol-narrow', '1.2-2', 'en-US')}`,
      headerStyle: {
        'text-align': 'center',
        'font-weight': 'bold',
        'max-width': '110px',
        'min-width': '100px',
      },
      cellStyle: {
        'text-align': 'center',
        'max-width': '110px',
        'min-width': '100px',
      },
    },
    {
      columnDef: 'monedaLocal',
      header: 'Moneda Local',
      cell: (element: any) => `${element.monedaLocal}`,
      headerStyle: {
        'text-align': 'center',
        'font-weight': 'bold',
        'max-width': '100px',
      },
      cellStyle: { 'text-align': 'center', 'max-width': '100px' },
    },
    {
      columnDef: 'monedaDestino',
      header: 'Moneda Destino',
      cell: (element: any) => `${element.monedaDestino}`,
      headerStyle: {
        'text-align': 'center',
        'font-weight': 'bold',
        'max-width': '100px',
      },
      cellStyle: { 'text-align': 'center', 'max-width': '100px' },
    },
    {
      columnDef: 'valorPagado',
      header: 'Valor Pagado',
      cell: (element: any) =>
        `${this.currencyPipe.transform(element.valorPagado, 'USD', 'symbol-narrow', '1.2-2', 'en-US')}`,
      headerStyle: {
        'text-align': 'center',
        'font-weight': 'bold',
        'max-width': '160px',
        'min-width': '140px',
      },
      cellStyle: {
        'text-align': 'left',
        'max-width': '160px',
        'min-width': '140px',
      },
    },
    {
      columnDef: 'tasa',
      header: 'Tasa',
      cell: (element: any) => `${element.tasa}`,
      headerStyle: {
        'text-align': 'center',
        'font-weight': 'bold',
        'max-width': '80px',
        'min-width': '60px',
      },
      cellStyle: {
        'text-align': 'center',
        'max-width': '80px',
        'min-width': '60px',
      },
    },
    {
      columnDef: 'walletTrx',
      header: 'Wallet',
      cell: (element: any) => `${element.walletTrx}`,
      headerStyle: {
        'text-align': 'center',
        'font-weight': 'bold',
        'max-width': '300px',
        'min-width': '260px',
      },
      cellStyle: {
        'text-align': 'left',
        'max-width': '300px',
        'min-width': '260px',
      },
    },
    {
      columnDef: 'estado',
      header: 'Estado',
      cell: (element: any) => {
        switch (element.estado) {
          case 'ABIERTA':
            return 'report';
          case 'CERRADA':
            return 'verified_user';
          default:
            return '';
        }
      },
      headerStyle: {
        'text-align': 'center',
        'font-weight': 'bold',
        'min-width': '90px',
        'max-width': '100px',
      },
      cellStyle: {
        'text-align': 'center',
        'min-width': '90px',
        'max-width': '100px',
        display: 'list-item',
      },
      iconColor: (element: any) => {
        switch (element.estado) {
          case 'ABIERTA':
            return '#EDD402';
          case 'CERRADA':
            return 'green';
          default:
            return '';
        }
      },
      val: (element: any) => `${element.estado}`,
    },
    {
      columnDef: 'fechaCreacion',
      header: 'Fecha de Creación',
      cell: (element: any) =>
        `${this.datePipe.transform(element.fechaCreacion, UtcToLocalTimeFormat.SHORT)}`,
      headerStyle: {
        'text-align': 'center',
        'font-weight': 'bold',
        'max-width': '100px',
        'min-width': '90px',
      },
      cellStyle: {
        'text-align': 'center',
        'max-width': '100px',
        'min-width': '90px',
      },
    },
    {
      columnDef: 'fechaFin',
      header: 'Fecha de Cierre',
      cell: (element: any) =>
        element.fechaFin
          ? `${this.datePipe.transform(element.fechaFin, UtcToLocalTimeFormat.SHORT)}`
          : ``,
      headerStyle: {
        'text-align': 'center',
        'font-weight': 'bold',
        'max-width': '100px',
        'min-width': '90px',
      },
      cellStyle: {
        'text-align': 'center',
        'max-width': '100px',
        'min-width': '90px',
      },
    },
    {
      columnDef: 'fechaActualizacion',
      header: 'Fecha de Actualizacion',
      cell: (element: any) =>
        `${this.datePipe.transform(element.fechaActualizacion, UtcToLocalTimeFormat.SHORT)}`,
      headerStyle: {
        'text-align': 'center',
        'font-weight': 'bold',
        'max-width': '150px',
        'min-width': '130px',
      },
      cellStyle: {
        'text-align': 'center',
        'max-width': '150px',
        'min-width': '130px',
      },
    },
  ];

  // ===== VES Methods =====

  obtenerMovimientosVes(page: number = 1): void {
    this.loadingVes = true;
    this.walletVesClientService
      .getMyMovements({ page, limit: this.pageSizeVes })
      .subscribe({
        next: (res) => {
          if (res?.success) {
            // Sin movimientos VES (chileno que nunca operó) llega vacío.
            this.movimientosVes = res.data?.items || [];
            this.totalMovimientosVes = res.data?.total || 0;
            this.totalPagesVes = res.data?.totalPages || 0;
            this.currentPageVes = res.data?.page || 1;
          }
          this.loadingVes = false;
        },
        error: () => {
          this.movimientosVes = [];
          this.loadingVes = false;
        },
      });
  }

  onPageChangeVes(event: PageEvent): void {
    this.currentPageVes = event.pageIndex + 1;
    this.pageSizeVes = event.pageSize;
    this.obtenerMovimientosVes(this.currentPageVes);
  }

  getVesStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      COMPLETED: 'Completado',
      PENDING: 'Pendiente',
      PROCESSING: 'Procesando',
      FAILED: 'Fallido',
      CANCELLED: 'Cancelado',
      REJECTED: 'Rechazado',
    };
    return labels[status] || status;
  }

  getVesStatusColor(status: string): string {
    const colors: { [key: string]: string } = {
      COMPLETED: '#16a34a',
      PENDING: '#f59e0b',
      PROCESSING: '#3b82f6',
      FAILED: '#dc2626',
      CANCELLED: '#6b7280',
      REJECTED: '#dc2626',
    };
    return colors[status] || '#6b7280';
  }

  private esAbonoVes(mov: VesMovementItem): boolean {
    return Number(mov.creditVes) > 0;
  }

  /**
   * El movimiento no movió el saldo: un ABONO PENDING (envío en curso) o
   * REJECTED (envío rechazado o anulado; si hubo devolución, va en su propia
   * fila). Un DÉBITO PENDING o REJECTED sí movió el saldo al crearse (p. ej. un
   * retiro VES en proceso; su devolución es otra fila).
   */
  vesSinEfectoEnSaldo(mov: VesMovementItem): boolean {
    return this.esAbonoVes(mov) && (mov.status === 'PENDING' || mov.status === 'REJECTED');
  }

  /** Aclaración del estado de un movimiento VES. */
  notaVesMovimiento(mov: VesMovementItem): string {
    if (this.esAbonoVes(mov)) {
      if (mov.status === 'PENDING') return 'En curso: no suma a tu saldo hasta que se complete.';
      if (mov.status === 'REJECTED') return 'No se aplicó: no afecta tu saldo VES.';
      return '';
    }
    if (mov.status === 'PENDING') return 'Descontado de tu saldo: en proceso.';
    if (mov.status === 'REJECTED') return 'Rechazado: el monto volvió a tu saldo con la devolución.';
    return '';
  }

  /**
   * Título de los movimientos que el sistema escribe con texto técnico. Los
   * envíos de chilenos a Venezuela (`ENVIO-CL-TX-<id>`) llevan tres asientos:
   * el abono (CLP→VES o USDT→VES), el pago al beneficiario (`-ENVIO`) y, si
   * el riel lo devolvió, la devolución en VES (`-REVERSO`).
   */
  private tituloVesPorReferencia(mov: VesMovementItem): string | null {
    const ref = String(mov.referenceId || '');
    if (ref.startsWith('ENVIO-CL-TX-')) {
      const tx = ref.slice('ENVIO-CL-TX-'.length).replace(/-(ENVIO|REVERSO)$/, '');
      if (ref.endsWith('-ENVIO')) return `Pago al beneficiario del envío #${tx}`;
      if (ref.endsWith('-REVERSO')) return `Devolución en VES del envío #${tx}`;
      const par = mov.type === 'SWAP_USDT_VES' ? 'USDT→VES' : 'CLP→VES';
      return `Envío a Venezuela #${tx} (${par})`;
    }
    if (ref.startsWith('REVERSO-COMPRA-VES-CLP-')) return 'Reverso de compra de bolívares';
    if (ref.startsWith('COMPRA-VES-CLP-')) return 'Compra de bolívares con CLP';
    return null;
  }

  sanitizeVesDescription(mov: VesMovementItem): string {
    const titulo = this.tituloVesPorReferencia(mov);
    if (titulo) return titulo;
    const desc = mov.description || '';
    const sanitized = desc.replace(/:\s*[\w.-]+@[\w.-]+\.\w+/g, '');
    if (sanitized.trim()) return sanitized.trim();
    if (mov.type) {
      const typeLabels: { [key: string]: string } = {
        CREDITO_MANUAL: 'Crédito manual VES',
        DEBITO_MANUAL: 'Débito manual VES',
        MANUAL_CREDIT: 'Crédito manual VES',
        MANUAL_DEBIT: 'Débito manual VES',
        SWAP_IN: 'Conversión recibida',
        SWAP_OUT: 'Conversión enviada',
        SWAP_USDT_VES: 'Swap USDT→VES',
        COMPRA_USDT_VES: 'Compra USDT (VES)',
        PAGO_MOVIL: 'Pago móvil',
        AJUSTE: 'Ajuste de saldo',
        RETIRO_VES: 'Retiro VES',
        REVERSO_RETIRO_VES: 'Reverso Retiro VES',
        ENVIO_VES_BENEFICIARIO: 'Envío VES Beneficiario',
        RETIRO_USDT_VES: 'Retiro USDT (VES)',
        SWAP_USDT_VES_WALLET: 'Swap USDT→VES',
        SWAP_CLP_VES_WALLET: 'Compra de bolívares con CLP',
        REVERSO_SWAP_CLP_VES_WALLET: 'Reverso de compra de bolívares',
      };
      return typeLabels[mov.type] || mov.type;
    }
    return mov.creditVes && mov.creditVes > 0 ? 'Credito VES' : 'Debito VES';
  }

  // ===== USDT Methods =====

  async obtenerMovimientosUsdt(page: number = 1): Promise<void> {
    this.loadingUsdt = true;
    if (this.tipoId === 'VE') {
      this.walletVesClientService
        .getMyUsdtMovements(page, this.pageSizeUsdt)
        .subscribe({
          next: (res) => {
            if (res.success) {
              this.movimientosUsdtCardex = res.data.items;
              this.usdtWithdrawals = res.data.withdrawals || [];
              this.usdtPendingSwaps = res.data.pendingSwaps || [];
              this.totalMovimientosUsdt = res.data.total;
              this.currentPageUsdt = res.data.page;
              this.buildUnifiedUsdtList();
            }
            this.loadingUsdt = false;
          },
          error: () => {
            this.movimientosUsdtCardex = [];
            this.usdtWithdrawals = [];
            this.usdtPendingSwaps = [];
            this.loadingUsdt = false;
          },
        });
    } else {
      // Non-VE users: use withdrawal history
      try {
        const obs = await this.binanceWithdrawalService.getWithdrawalHistory(page, this.pageSizeUsdt);
        const res = await firstValueFrom(obs);
        this.movimientosUsdt = res.items;
        this.totalMovimientosUsdt = res.total;
        this.currentPageUsdt = res.page;
      } catch {
        this.movimientosUsdt = [];
      } finally {
        this.loadingUsdt = false;
      }
    }
  }

  onPageChangeUsdt(event: PageEvent): void {
    this.currentPageUsdt = event.pageIndex + 1;
    this.pageSizeUsdt = event.pageSize;
    this.obtenerMovimientosUsdt(this.currentPageUsdt);
  }

  /**
   * Estado a mostrar para un movimiento de cardex. Los payouts USDT→COP
   * (PAYOUT_USDT_COP) traen `payoutEstado` de la transacción ligada: mientras
   * Vita no confirme, el cliente lo ve Pendiente; luego Enviado o Rechazado.
   * El resto de asientos del ledger quedan 'completed'.
   */
  private cardexMovementStatus(mov: UsdtCardexItem): string {
    if (mov.network === 'PAYOUT_USDT_COP') {
      switch (mov.payoutEstado) {
        case 'POR_PROCESAR':
          return 'pending';
        case 'ENVIADA':
          return 'sent';
        case 'RECHAZADA':
          return 'rejected';
      }
    }
    return 'completed';
  }

  getUsdtStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      pending: 'Pendiente',
      processing: 'Procesando',
      sent: 'Enviado',
      completed: 'Completado',
      rejected: 'Rechazado',
      cancelled: 'Cancelado',
    };
    return labels[status] || status;
  }

  getUsdtStatusClass(status: string): string {
    const classes: { [key: string]: string } = {
      pending: '#f59e0b',
      processing: '#3b82f6',
      sent: '#16a34a',
      completed: '#16a34a',
      rejected: '#dc2626',
      cancelled: '#6b7280',
    };
    return classes[status] || '#6b7280';
  }

  getCardexNetworkLabel(network: string): string {
    const labels: { [key: string]: string } = {
      SWAP_USDT_VES: 'Swap USDT→VES',
      REVERSO_SWAP_USDT_VES: 'Reverso Swap USDT→VES',
      SWAP_VES_TO_USDT: 'Compra USDT (VES)',
      COMPRA_USDT_VES: 'Compra USDT (VES)',
      SEND_USDT: 'Envío USDT',
      DEPOSIT: 'Depósito',
      CREDITO_MANUAL: 'Crédito Manual',
      DEBITO_MANUAL: 'Débito Manual',
      MANUAL_CREDIT: 'Crédito Manual',
      MANUAL_DEBIT: 'Débito Manual',
      ADD_USDT_MANUAL: 'Crédito Manual USDT',
      DEBIT_USDT_MANUAL: 'Débito Manual USDT',
      TRX: 'Depósito TRX',
      RETIRO_USDT_VES: 'Retiro USDT (VES)',
      PAYOUT_USDT_COP: 'Envío a Colombia',
      REVERSO_USDT_COP: 'Reverso envío a Colombia',
    };
    return labels[network] || network;
  }

  getWithdrawalStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      pending: 'Pendiente',
      processing: 'Procesando',
      completed: 'Completado',
      rejected: 'Rechazado',
      cancelled: 'Cancelado',
    };
    return labels[status] || status;
  }

  getWithdrawalStatusColor(status: string): string {
    const colors: { [key: string]: string } = {
      pending: '#f59e0b',
      processing: '#3b82f6',
      completed: '#16a34a',
      rejected: '#dc2626',
      cancelled: '#6b7280',
    };
    return colors[status] || '#6b7280';
  }

  buildUnifiedUsdtList(): void {
    const fromCardex: UnifiedUsdtMovement[] = this.movimientosUsdtCardex.map(mov => ({
      source: 'cardex' as const,
      date: mov.createdAt,
      label: this.getCardexNetworkLabel(mov.network),
      amount: mov.haber > 0 ? mov.haber : mov.debe,
      isCredit: mov.haber > 0,
      // Los payouts USDT→COP reflejan su ciclo de vida (Pendiente/Enviado/
      // Rechazado), no el ledger; el resto de asientos quedan Completado.
      status: this.cardexMovementStatus(mov),
      haber: mov.haber,
      debe: mov.debe,
      saldo: mov.saldo,
      description: mov.description,
      network: mov.network,
    }));

    const fromWithdrawals: UnifiedUsdtMovement[] = this.usdtWithdrawals.map(w => ({
      source: 'withdrawal' as const,
      date: w.requestedAt || w.createdAt,
      label: `Retiro ${w.network}`,
      amount: w.requestedAmount,
      isCredit: false,
      status: w.status,
      walletAddress: w.walletAddress,
      fee: w.fee,
      netAmount: w.netAmount,
      network: w.network,
      binanceTxId: w.binanceTxId,
      rejectionReason: w.rejectionReason,
    }));

    this.unifiedUsdtMovements = [...fromCardex, ...fromWithdrawals]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }

  sanitizePendingSwapDescription(swap: UsdtPendingSwapItem): string {
    const desc = swap.description || '';
    const swapMatch = desc.match(/Swap\s+([\d.]+)\s+USDT\s*→\s*([\d.,]+)\s*VES/i);
    if (swapMatch) {
      return `Swap ${swapMatch[1]} USDT → ${swapMatch[2]} VES`;
    }
    const typeLabels: { [key: string]: string } = {
      SWAP_USDT_VES: 'Swap USDT → VES',
      COMPRA_USDT_VES: 'Compra USDT (VES)',
      RETIRO_USDT_VES: 'Retiro USDT (VES)',
    };
    return typeLabels[swap.type] || swap.type || 'Operación pendiente';
  }
}
